import React, { useContext, useEffect, useRef, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import QRCode from 'qrcode'
import UserContext from '../context/UserContext.js'
import useLearnerContext from '../hooks/useLearnerContext'
import {
  GET_MY_PHRASES_QUERY, RECORD_PRACTICE_MUTATION, CHAT_MUTATION,
  MY_SPEAKING_TASKS_QUERY, MY_STUDY_HOMEWORK_QUERY, STUDY_TASK_PHRASES_QUERY, MY_ASSIGNMENTS_QUERY, MY_LESSON_PROGRESS_QUERY,
  GET_MEETING_WITH_CONTENT_QUERY, RECORD_LESSON_RESULTS_MUTATION, RECORD_SPEAKING_RESULTS_MUTATION,
  MY_SENTENCE_BUILDS_QUERY, SENTENCE_BUILD_QUERY, SENTENCE_MAP_QUERY, START_SENTENCE_BUILD_MUTATION, SAVE_SENTENCE_BUILD_MUTATION,
} from '../query/gqlQueries.js'
import languages from '../languages'
import voices from '../speech/voices'
import { speakTracked, stopSpeaking } from '../speech/speak'
import { mergeTranscript, wordUnits, romanizationOf, tokenizeLine, tokenizeTranscript, tokenMatches } from '../speech/flowMatch'
import { ROLES } from './SentenceBuilderView.js'
import s from './BigScreenView.module.scss'

// The Big screen: this browser as the Apple TV, the iPhone as its remote.
//
// The phone app already drives Communicate on an Apple TV (Bonjour on the
// home network, TVLink JSON messages). A web page can't be found on the
// network or take a connection, so the server relays instead
// (lib/remoteRelay.js): this page opens a session and shows it as a QR code,
// the phone scans it with its camera and joins — and from then on the two
// speak exactly what the TV and the phone speak. The phone is the
// microphone (it sends what it hears) and, by default, the speaker (the
// phone's voices are better); the practice is up here, big.
//
// The practices are the TV's (CommunicateTV/TVPractice.swift), with its
// rules: nothing moves on by itself, only a phrase said in full without help
// counts as said, "Not yet" comes round again at the end, and the end of a
// round lists what to practise.

const MODES = [
  { screen: 'listen', title: 'Listen', blurb: 'Each phrase said twice, then what it means' },
  { screen: 'gaps', title: 'Fill the gaps', blurb: 'Some words missing — say the whole phrase' },
  { screen: 'memory', title: 'From memory', blurb: 'Only the meaning — say it in full' },
  { screen: 'quiz', title: 'Quiz', blurb: 'Hear it, pick what it means' },
  { screen: 'talk', title: 'Talk', blurb: 'A conversation, out loud — gently corrected' },
  { screen: 'build', title: 'Build a sentence', blurb: 'Say what you want to say, then build it block by block' },
]
const LANG_KEY = 'bigScreen.lang'

// Talk: Language Partner, as on the TV (CommunicateTV/TVTalk.swift). The
// phone shows the label; the model gets the fuller topic.
const TOPICS = [
  { key: 'your day — what you did today and what\'s next', label: 'Your day' },
  { key: 'last weekend and plans for the next one', label: 'The weekend' },
  { key: 'food, cooking and eating out', label: 'Food' },
  { key: 'travel — places you\'ve been and want to go', label: 'Travel' },
  { key: 'work or studies', label: 'Work and studies' },
  { key: 'hobbies and free time', label: 'Hobbies' },
]
const PAUSE_TO_ANSWER_MS = 1600
const CHAT_MODEL = 'gemma4:26b'

// The same instructions as the phone and the TV (Shared/PartnerModel.swift).
const partnerPrompt = ({ target, own, level, topic }) => `You are a warm, patient ${target} conversation partner, talking out loud with a learner whose own language is ${own} and whose ${target} is about ${level}. The conversation is about: ${topic}.
This is a voice conversation: their words reach you through speech recognition, so ignore missing punctuation, capital letters and small recognition slips — never correct those. Everything you write is read aloud, so write only what should be said: no lists, no emoji, no romanisation, no stage directions.

Answer every turn as JSON with these fields:
- "fix": if their last message had a real mistake (grammar, a wrong word, something a native wouldn't say), ONE short, friendly sentence in ${own} naming the most important one — for example "Small fix — say it like this:". Don't write the ${target} words here. Otherwise "".
- "better": when there is a fix, the corrected version of what they meant, in ${target} — just that phrase or sentence. Otherwise "".
- "reply": your answer in ${target}: one or two short spoken sentences at their level, reacting to what they said and ending with a question or a nudge that keeps them talking.
- "mistakes": every real mistake in their last message, as {"said": their words, "better": the correct ${target}, "why": a few words in ${own}}. [] if none.
Return ONLY minified JSON: {"fix":"","better":"","reply":"","mistakes":[]}`

// The model's answer: JSON, or — if it isn't — the reply itself.
const parseTurn = (raw) => {
  const text = String(raw || '')
  const a = text.indexOf('{')
  const b = text.lastIndexOf('}')
  let o = null
  if (a >= 0 && b > a) { try { o = JSON.parse(text.slice(a, b + 1)) } catch { o = null } }
  if (!o || typeof o !== 'object') return { fix: '', better: '', reply: text.trim(), mistakes: [] }
  const str = (k) => String(o[k] == null ? '' : o[k]).trim()
  const mistakes = (Array.isArray(o.mistakes) ? o.mistakes : []).map(m => ({
    said: String(m?.said || '').trim(), better: String(m?.better || '').trim(), why: String(m?.why || '').trim(),
  })).filter(m => m.said && m.better && m.said.toLowerCase() !== m.better.toLowerCase())
  const fix = str('fix')
  const better = str('better')
  // A correction without the corrected words isn't one.
  return { fix: better ? fix : '', better: fix ? better : '', reply: str('reply'), mistakes }
}
const roleAsk = (r) => (ROLES[r] || ROLES.other).ask
const roleColor = (r) => (ROLES[r] || ROLES.other).color

/** The server's /remote, next to its /graphql. */
export const relayUrl = () => {
  const u = new URL(process.env.REACT_APP_DB_URI || 'http://localhost:4000/graphql', window.location.href)
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:'
  u.pathname = '/remote'
  u.search = ''
  return u.toString()
}

const stripGloss = (t) => String(t || '').replace(/\(([^)]*[A-Za-z][^)]*)\)/g, '').trim()
// The learner's side of a phrase first (DueItem.card(learning:) on the phone).
const cardOf = (item, lang) => {
  if (item.lang === lang) return [item.text, item.translationText]
  if (item.translationText) return [item.translationText, item.text]
  return [item.text, item.translationText]
}
const shuffle = (a) => {
  const b = [...a]
  for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[b[i], b[j]] = [b[j], b[i]] }
  return b
}
const ordered = (list, first) => {
  const l = shuffle(list)
  const i = l.findIndex(p => p.id === first)
  if (i > 0) l.unshift(l.splice(i, 1)[0])
  return l
}
const sleep = (ms) => new Promise(r => setTimeout(r, ms))
const voiceFor = (lang) => voices[lang] || voices.eng
const cpsFor = (lang) => (['kor', 'jpn', 'cmn'].includes(lang) ? 4.5 : 13)
const sayable = (text) => String(text || '').replace(/["“”«»„‹›]/g, ' ')
// The first part of a missing word a hint shows: a quarter, then a half.
const hintPrefix = (word, level) => {
  const n = Array.from(word).length
  if (level <= 0 || n <= 1) return 0
  return Math.min(n - 1, Math.max(1, Math.ceil(n * (level >= 2 ? 0.5 : 0.25))))
}
// How a phrase went (SayOutcome): only clean and nearly count as said.
const outcomeOf = ({ correct, allSaid, nearly, revealedFirst, hints }) => {
  if (!correct) return 'missed'
  if (hints > 0 || revealedFirst) return 'helped'
  if (allSaid) return 'clean'
  return nearly ? 'nearly' : 'helped'
}
const isSaid = (o) => o === 'clean' || o === 'nearly'

export default function BigScreenView({ voiceSpeed, onClose }) {
  const { username } = useContext(UserContext)
  const { native, learning } = useLearnerContext(username)
  const client = useApolloClient()
  const [record] = useMutation(RECORD_PRACTICE_MUTATION)
  const [recordLesson] = useMutation(RECORD_LESSON_RESULTS_MUTATION)
  const [recordSpeaking] = useMutation(RECORD_SPEAKING_RESULTS_MUTATION)
  const myLangs = (learning || []).filter(l => l && l.code).map(l => l.code)
  // My level in a language: up to A2 a block's note waits for Next; from B1 it moves on.
  const beginnerIn = (code) => {
    const level = String(((learningRef.current || []).find(l => l && l.code === code) || {}).level || '').toUpperCase()
    const m = /^([ABC])(\d)/.exec(level)
    return !m || (m[1] === 'A' && Number(m[2]) <= 2)
  }
  const nativeCode = native?.[0]?.code || 'eng'

  // Everything the page and the phone share lives here, so the socket's
  // handlers always see it as it is now; `render` redraws.
  const g = useRef(null)
  if (!g.current) {
    let saved = ''
    try { saved = localStorage.getItem(LANG_KEY) || '' } catch { /* private window */ }
    g.current = {
      ws: null, session: null, code: null, status: 'connecting', error: '', paired: false, phoneName: null,
      audioOnPhone: false, speed: typeof voiceSpeed === 'number' && voiceSpeed > 0 ? voiceSpeed : 1,
      lang: saved, deck: [], loading: false, screen: 'home', gen: 0, saying: null,
      speechSeq: 0, pending: new Map(), lastSent: '', closing: false,
      listen: { order: [], index: 0, paused: false, meaningShown: false },
      say: null, quiz: null, talk: null, build: null,
      // Homework, as the Apple TV lists it for the phone; and, while one is
      // being practised, the deck and language to go back to.
      homework: [], speakingTasks: [], hw: null, homeSaved: null,
    }
  }
  const [, setTick] = useState(0)
  const render = () => setTick(t => t + 1)
  const [qr, setQr] = useState('')
  // What's typed here: a Talk answer, and what to build.
  const [talkDraft, setTalkDraft] = useState('')
  const [buildDraft, setBuildDraft] = useState('')
  const myLangsRef = useRef(myLangs)
  myLangsRef.current = myLangs
  const learningRef = useRef(learning)
  learningRef.current = learning
  const nativeRef = useRef(nativeCode)
  nativeRef.current = nativeCode
  const lang = g.current.lang || myLangs[0] || ''

  // MARK: talking to the phone

  const send = (obj) => {
    const ws = g.current.ws
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(obj))
  }

  const baseState = (screen) => ({
    screen, lang: g.current.lang || myLangsRef.current[0] || 'eng',
    revealed: false, paused: false, index: 0, total: 0, right: 0, listening: false,
  })

  const stateNow = () => {
    const G = g.current
    if (G.screen === 'home') {
      return {
        ...baseState('home'),
        languages: myLangsRef.current,
        phrases: G.deck.slice(0, 150).map(p => ({ id: p.id, text: p.text, meaning: p.meaning })),
        modes: MODES.map(m => m.screen),
        homework: G.homework,
      }
    }
    if (G.screen === 'listen') {
      const L = G.listen
      const p = L.order[L.index]
      return { ...baseState('listen'), text: p?.text, meaning: p?.meaning, revealed: L.meaningShown, paused: L.paused, index: L.index, total: L.order.length }
    }
    if (G.screen === 'gaps' || G.screen === 'memory') {
      const S = G.say
      const p = S && S.round[S.index]
      if (!p) {
        return {
          ...baseState('done'),
          phrases: toPractise().map(x => ({ id: x.id, text: x.text, meaning: x.meaning })),
          total: thisRound().length, right: saidCount(),
          note: helpedCount() > 0 ? `${helpedCount()} more with help — hints, or after the answer` : null,
        }
      }
      return {
        ...baseState(G.screen), text: p.text, meaning: p.meaning,
        words: S.units.map(u => u.raw), hidden: [...S.hidden].sort((a, b) => a - b),
        hits: S.units.map((_, i) => i).filter(wordHeard),
        revealed: S.revealed, index: S.index, total: S.round.length, right: saidCount(),
        listening: !S.revealed || practising(), hint: S.hint, saying: G.saying,
        nearly: S.nearly, allSaid: S.saidRight || S.practisedAll,
      }
    }
    if (G.screen === 'talk' && G.talk) {
      const T = G.talk
      const last = [...T.lines].reverse().find(l => !l.mine)
      return {
        ...baseState(T.state === 'choosing' ? 'talkTopics' : 'talk'),
        text: last?.text || null, paused: T.state === 'paused',
        options: T.state === 'choosing' ? TOPICS.map(t => t.label) : null,
        listening: T.state === 'listening',
        fix: last?.fix || null, better: last?.better || null,
        heard: T.heard || (T.state === 'failed' ? T.error : null) || null,
        thinking: T.state === 'thinking',
      }
    }
    if (G.screen === 'build' && G.build) {
      const B = G.build
      const blocks = B.build?.blocks || []
      const cur = currentBlock()
      const screen = { ask: 'buildAsk', making: 'building', build: 'build', done: 'buildDone' }[B.phase]
      return {
        ...baseState(screen),
        phrases: B.recent.map(r => ({ id: r.id, text: r.sentence || '', meaning: r.text })),
        text: B.text, meaning: B.build?.meaning || null,
        words: B.phase === 'done' ? B.units.map(u => u.raw) : null,
        hits: B.phase === 'done' ? B.units.map((_, i) => i).filter(builtWordHeard) : null,
        revealed: B.justBuilt,
        options: cur ? B.options : null,
        picked: [...B.wrong].sort().join('\n'),
        answer: B.justBuilt && cur ? cur.said : null,
        index: B.step, total: blocks.length, right: B.mistakes,
        listening: (B.phase === 'build' && !B.justBuilt) || (B.phase === 'done' && !buildAllSaid()),
        heard: B.error || null,
        thinking: B.phase === 'making',
        saying: G.saying,
        allSaid: B.phase === 'done' ? buildAllSaid() : null,
        blocks: blocks.length ? blocks.map((b, i) => ({ role: b.role, said: isBuilt(i) ? b.said : null, meaning: b.meaning || null,
          base: isBuilt(i) ? b.base || null : null, note: isBuilt(i) ? b.note || null : null })) : null,
        question: cur ? roleAsk(cur.role) + (cur.meaning ? ` “${cur.meaning}”` : '') : null,
        note: B.justBuilt && cur ? [cur.base ? `← ${cur.base}` : null, cur.note].filter(Boolean).join(' — ') : null,
        saved: B.saved,
      }
    }
    if (G.screen === 'quiz') {
      const Q = G.quiz
      const p = Q && Q.round[Q.index]
      if (!p) return { ...baseState('done'), total: Q ? Q.round.length : 0, right: Q ? Q.right : 0 }
      return { ...baseState('quiz'), text: p.text, options: Q.options, picked: Q.picked, answer: Q.picked == null ? null : p.meaning, index: Q.index, total: Q.round.length, right: Q.right }
    }
    return baseState('home')
  }

  /** What's on the screen now, to the phone — unless it's what it last got. */
  const publish = (force = false) => {
    const G = g.current
    if (!G.paired) return
    const json = JSON.stringify({ kind: 'state', state: stateNow() })
    if (!force && json === G.lastSent) return
    G.lastSent = json
    send(JSON.parse(json))
  }

  const update = () => { publish(); render() }

  // MARK: speech — on the phone when it's there and wants it, else here

  const finishSpeech = (id) => {
    const done = g.current.pending.get(id)
    if (done) { g.current.pending.delete(id); done() }
  }
  const finishAllSpeech = () => {
    for (const id of [...g.current.pending.keys()]) finishSpeech(id)
  }

  const say = (text, sayLang, slow = false) => {
    const G = g.current
    if (!text) return Promise.resolve()
    if (G.paired && G.audioOnPhone) {
      const id = ++G.speechSeq
      send({ kind: 'speak', speech: [{ text, lang: sayLang, slow }], speechId: id })
      return new Promise(resolve => {
        G.pending.set(id, resolve)
        // A phone that never says it's done mustn't stall the screen.
        setTimeout(() => finishSpeech(id), 5000 + text.length * 250)
      })
    }
    const units = currentUnits(text)
    const t = speakTracked(sayable(text), voiceFor(sayLang), G.speed * (slow ? 0.75 : 1), {
      cps: cpsFor(sayLang),
      onBoundary: ({ charIndex }) => {
        if (!units) return
        let k = null
        units.forEach((u, i) => { if (u.start <= charIndex) k = i })
        if (k !== G.saying) { G.saying = k; update() }
      },
      onEnd: () => { if (G.saying != null) { G.saying = null; update() } },
    })
    return t.promise
  }

  // The words of the phrase on screen, if it's the one being said (to light them).
  const currentUnits = (text) => {
    const G = g.current
    if (G.screen === 'build') {
      const B = G.build
      return B && B.phase === 'done' && B.build?.sentence === text ? B.units : null
    }
    const S = G.say
    const p = S && S.round[S.index]
    return p && p.text === text ? S.units : null
  }

  const hush = () => {
    const G = g.current
    if (G.paired && G.audioOnPhone) send({ kind: 'hush' })
    stopSpeaking()
    finishAllSpeech()
    G.saying = null
  }

  const stopAll = () => {
    const G = g.current
    G.gen++
    if (G.talk) clearInterval(G.talk.watcher)
    if (G.build) clearTimeout(G.build.advance)
    hush()
  }

  // MARK: the deck

  const loadDeck = async (code) => {
    const G = g.current
    if (!code) return
    G.loading = true
    render()
    try {
      const { data } = await client.query({ query: GET_MY_PHRASES_QUERY, variables: { lang: code, limit: 300 }, fetchPolicy: 'network-only' })
      const seen = new Set()
      G.deck = (data?.getMyPhrases || []).map(item => {
        const [front, back] = cardOf(item, code)
        const text = stripGloss(front)
        if (!text || seen.has(text.toLowerCase())) return null
        seen.add(text.toLowerCase())
        return { id: item.id, text, meaning: back ? stripGloss(back) : null, itemType: item.itemType }
      }).filter(Boolean)
    } catch (e) {
      G.deck = []
      G.error = e.message
    }
    G.loading = false
    update()
  }

  const setLang = (code) => {
    const G = g.current
    G.homeSaved = null
    G.hw = null
    if (!code) return
    G.lang = code
    try { localStorage.setItem(LANG_KEY, code) } catch { /* private window */ }
    stopAll()
    G.screen = 'home'
    G.deck = []
    update()
    loadDeck(code)
  }

  const recordAnswer = (p, correct) => {
    const done = () => { /* offline: dropped, as on the TV */ }
    // A homework's answers count towards its percentage, as on the TV.
    if (p.hw?.kind === 'speaking') {
      const mode = g.current.screen === 'memory' ? 'memory' : 'gaps'
      recordSpeaking({ variables: { taskId: p.hw.id, results: [{ key: p.text, mode, correct }] } }).catch(done)
      return
    }
    if (p.hw?.kind === 'lesson') {
      recordLesson({ variables: { meetingId: p.hw.id, results: [{ key: p.id, correct }] } }).catch(done)
      return
    }
    record({ variables: { itemId: p.id, itemType: p.itemType || 'Sentence', isCorrect: correct } }).catch(done)
  }

  // MARK: homework

  // Speaking homework, set lessons not handed in, and practice homework —
  // the list the phone shows under Homework, as from the Apple TV.
  const loadHomework = async () => {
    const G = g.current
    const q = (query, variables) => client.query({ query, variables, fetchPolicy: 'network-only' }).then(r => r.data).catch(() => null)
    const [sp, st, as] = await Promise.all([q(MY_SPEAKING_TASKS_QUERY), q(MY_STUDY_HOMEWORK_QUERY), q(MY_ASSIGNMENTS_QUERY)])
    const speaking = (sp?.mySpeakingTasks || []).filter(t => (t.topics || []).some(x => (x.lines || []).length))
    const study = (st?.myStudyTasks || []).filter(t => t.status === 'ready')
    const lessons = (as?.myAssignments || []).filter(a => a.status !== 'submitted' && a.status !== 'reviewed')
    const progress = lessons.length ? await q(MY_LESSON_PROGRESS_QUERY, { ids: lessons.map(a => String(a.meetingId)) }) : null
    const pctOf = (id) => (progress?.myLessonProgress || []).find(r => String(r.meetingId) === String(id))?.pct ?? 0
    G.speakingTasks = speaking
    G.homework = [
      ...speaking.map(t => ({ kind: 'speaking', ref: t.id, title: t.title || 'Speaking homework', lang: t.lang, pct: t.progress ?? 0, due: t.due || null })),
      ...lessons.map(a => ({ kind: 'lesson', ref: String(a.meetingId), title: a.title, lang: null, pct: pctOf(a.meetingId), due: a.due || null })),
      ...study.map(t => ({ kind: 'study', ref: t.id, title: t.title, lang: t.lang, pct: t.progress ?? 0, due: t.due || null })),
    ]
    if (G.screen === 'home') publish(true)
  }

  // One homework's phrases, practised in a mode; Home brings the deck back.
  const openHomework = async (value) => {
    const G = g.current
    const [kind, ref, modeRaw] = String(value || '').split('|')
    const mode = MODES.some(m => m.screen === modeRaw) ? modeRaw : 'gaps'
    let phrases = []
    let lang = null
    try {
      if (kind === 'speaking') {
        const t = G.speakingTasks.find(x => x.id === ref)
        if (!t) return
        lang = t.lang
        phrases = (t.topics || []).flatMap(x => x.lines || []).filter(l => l.said)
          .map((l, i) => ({ id: `sp:${ref}:${i}`, text: l.said, meaning: l.meaning || null, hw: { kind, id: ref } }))
      } else if (kind === 'study') {
        const { data } = await client.query({ query: STUDY_TASK_PHRASES_QUERY, variables: { id: ref }, fetchPolicy: 'network-only' })
        lang = data?.studyTask?.lang
        phrases = [...(data?.studyTask?.phrases || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).filter(p => p.translation)
          .map(p => ({ id: p.sentenceId || p.id, text: p.translation, meaning: p.text || null, itemType: 'Sentence' }))
      } else if (kind === 'lesson') {
        const { data } = await client.query({ query: GET_MEETING_WITH_CONTENT_QUERY, variables: { identity: ref }, fetchPolicy: 'network-only' })
        let lesson = {}
        try { lesson = JSON.parse(data?.getMeetingWithContent?.meeting?.properties?.lessonData || '{}') } catch { lesson = {} }
        lang = lesson.language || null
        const seen = new Set()
        for (const section of lesson.sections || []) {
          for (const item of section.exercise?.items || []) {
            const a = String(item.a || '').trim()
            const key = item.key || item.q
            if (!a || !item.q || seen.has(key)) continue
            seen.add(key)
            phrases.push({ id: key, text: a, meaning: item.q, hw: { kind, id: ref } })
          }
        }
      }
    } catch (e) {
      G.error = e.message
    }
    if (!phrases.length) { publish(true); return }
    if (!G.homeSaved) G.homeSaved = { deck: G.deck, lang: G.lang }
    G.hw = { kind, ref }
    G.deck = phrases
    if (lang) G.lang = lang
    open(mode)
  }

  // Back from a homework: my phrases and language again, and its new percentage.
  const leaveHomework = () => {
    const G = g.current
    if (!G.homeSaved) return
    G.deck = G.homeSaved.deck
    G.lang = G.homeSaved.lang
    G.homeSaved = null
    G.hw = null
    loadHomework()
  }

  // MARK: listen

  const startListen = () => {
    const G = g.current
    const gen = ++G.gen
    const L = G.listen
    ;(async () => {
      while (G.gen === gen && L.order.length) {
        const p = L.order[L.index]
        L.meaningShown = false
        update()
        await say(p.text, G.lang)
        if (G.gen !== gen) return
        await sleep(1200)
        if (G.gen !== gen) return
        await say(p.text, G.lang, true)
        if (G.gen !== gen) return
        L.meaningShown = true
        update()
        if (p.meaning) {
          await sleep(800)
          if (G.gen !== gen) return
          await say(p.meaning, nativeRef.current)
        }
        await sleep(2500)
        if (G.gen !== gen) return
        L.index = (L.index + 1) % L.order.length
        if (L.index === 0) L.order = shuffle(L.order)
      }
    })()
  }

  const listenGo = (step) => {
    const L = g.current.listen
    if (!L.order.length) return
    stopAll()
    L.index = ((L.index + step) % L.order.length + L.order.length) % L.order.length
    L.meaningShown = false
    L.paused = false
    startListen()
    update()
  }

  const listenPause = () => {
    const L = g.current.listen
    L.paused = !L.paused
    if (L.paused) stopAll(); else startListen()
    update()
  }

  // MARK: fill the gaps / from memory

  const thisRound = () => {
    const S = g.current.say
    if (!S) return []
    const seen = new Set()
    return S.round.filter(p => !seen.has(p.id) && seen.add(p.id))
  }
  const saidCount = () => thisRound().filter(p => isSaid(g.current.say.outcomes[p.id])).length
  const helpedCount = () => thisRound().filter(p => g.current.say.outcomes[p.id] === 'helped').length
  const toPractise = () => thisRound().filter(p => !isSaid(g.current.say.outcomes[p.id]))
  const practising = () => { const S = g.current.say; return S.revealed && !S.saidRight && !S.practisedAll }
  const wordHeard = (i) => {
    const S = g.current.say
    const u = S && S.units[i]
    if (!u || u.to <= u.from) return false
    for (let k = u.from; k < u.to; k++) if (S.states[k] !== 'hit') return false
    return true
  }

  const beginSay = (allHidden, only, first) => {
    const G = g.current
    const pool = allHidden ? G.deck.filter(p => p.meaning) : G.deck
    G.say = {
      allHidden, round: only ? [...only] : ordered(pool, first).slice(0, 20), index: 0,
      outcomes: {}, retried: new Set(), units: [], tokens: [], states: [], hidden: new Set(),
      revealed: false, revealedFirst: false, saidRight: false, nearly: false, practisedAll: false, hint: 0,
    }
    prepareSay()
  }

  const prepareSay = () => {
    const G = g.current
    const S = G.say
    Object.assign(S, { revealed: false, revealedFirst: false, saidRight: false, nearly: false, practisedAll: false, hint: 0 })
    G.saying = null
    const p = S.round[S.index]
    if (!p) { S.units = []; S.tokens = []; S.states = []; S.hidden = new Set(); update(); return }
    const { units, tokens, spaced } = wordUnits(p.text, G.lang)
    S.spaced = spaced
    S.units = units
    S.tokens = tokens
    S.states = tokens.map(() => 'pending')
    // Only real words are ever gaps — never a dash.
    const real = units.map((u, i) => i).filter(i => units[i].to > units[i].from)
    if (S.allHidden || real.length <= 1) {
      S.hidden = new Set(real)
    } else {
      // Every other word, starting at random — at least one, never all.
      const start = Math.random() < 0.5 ? 0 : 1
      const h = new Set()
      for (let k = start; k < real.length; k += 2) h.add(real[k])
      if (h.size === real.length) h.delete(real[0])
      S.hidden = h
    }
    update()
  }

  const revealSay = () => {
    const S = g.current.say
    const p = S && S.round[S.index]
    if (!p || S.revealed) return
    if (!S.saidRight) S.revealedFirst = true
    S.revealed = true
    update()
    say(p.text, g.current.lang)
  }

  const answerSay = (correct) => {
    const S = g.current.say
    const p = S && S.round[S.index]
    if (!p) return
    const outcome = outcomeOf({ correct, allSaid: S.saidRight, nearly: S.nearly, revealedFirst: S.revealedFirst, hints: S.hint })
    S.outcomes[p.id] = outcome
    recordAnswer(p, isSaid(outcome) || (S.saidRight && !S.revealedFirst))
    // "Not yet": once more, at the end of the round.
    if (outcome === 'missed' && !S.retried.has(p.id)) { S.retried.add(p.id); S.round.push(p) }
    hush()
    S.index += 1
    prepareSay()
  }

  /** What the phone heard: light up the words; all of them shows the answer. */
  const heard = (text) => {
    if (g.current.screen === 'talk') return talkHeard(text)
    if (g.current.screen === 'build') return buildHeard(text)
    const S = g.current.say
    if (!S || !['gaps', 'memory'].includes(g.current.screen)) return
    const p = S.round[S.index]
    if (!p || !S.tokens.length || S.saidRight || S.practisedAll) return
    S.states = mergeTranscript(S.states, text, S.tokens, g.current.lang, false)
    const hits = S.states.filter(x => x === 'hit').length
    if (S.revealed) {
      if (hits >= S.tokens.length) S.practisedAll = true
      update()
      return
    }
    if (hits >= S.tokens.length) {
      S.saidRight = true
      revealSay()
    } else if (hits >= S.tokens.length * 0.9) {
      S.nearly = true
    }
    update()
  }

  const sayCommand = (action, value) => {
    const G = g.current
    const S = G.say
    const p = S && S.round[S.index]
    switch (action) {
      case 'reveal': revealSay(); break
      case 'right': if (S.revealed || S.nearly) answerSay(true); break
      case 'wrong': if (S.revealed) answerSay(false); break
      case 'next': if (S.revealed) answerSay(false); else { S.index += 1; prepareSay() } break
      case 'again': if (!p) beginSay(S.allHidden); break
      case 'againMissed': if (!p && toPractise().length) beginSay(S.allHidden, toPractise()); break
      case 'hear': if (p) say(p.text, G.lang); break
      case 'hint': if (S.hint < 2 && !S.revealed) { S.hint += 1; update() } break
      case 'phrase': {
        const q = G.deck.find(x => x.id === value)
        if (q) { S.round.splice(Math.min(S.index, S.round.length), 0, q); prepareSay() }
        break
      }
      default: break
    }
  }

  // MARK: quiz

  const quizPool = () => g.current.deck.filter(p => p.meaning)

  const beginQuiz = (first) => {
    g.current.quiz = { round: ordered(quizPool(), first).slice(0, 15), index: 0, options: [], picked: null, right: 0 }
    prepareQuiz()
  }

  const prepareQuiz = () => {
    const Q = g.current.quiz
    Q.picked = null
    const p = Q.round[Q.index]
    if (!p) { update(); return }
    const set = [p.meaning]
    for (const other of shuffle(quizPool())) {
      if (set.length >= 4) break
      if (other.meaning && !set.some(m => m.toLowerCase() === other.meaning.toLowerCase())) set.push(other.meaning)
    }
    Q.options = shuffle(set)
    update()
    say(p.text, g.current.lang)
  }

  const chooseQuiz = (option) => {
    const Q = g.current.quiz
    const p = Q && Q.round[Q.index]
    if (!p || Q.picked != null) return
    Q.picked = option
    const correct = option === p.meaning
    if (correct) Q.right += 1
    recordAnswer(p, correct)
    update()
  }

  const quizCommand = (action, value) => {
    const Q = g.current.quiz
    const p = Q && Q.round[Q.index]
    switch (action) {
      case 'pick': chooseQuiz(value); break
      case 'next': if (Q.picked != null) { Q.index += 1; prepareQuiz() } break
      case 'again': if (!p) beginQuiz(); break
      case 'hear': if (p) say(p.text, g.current.lang); break
      default: break
    }
  }

  // MARK: talk

  const newTalk = () => ({ state: 'choosing', topic: '', lines: [], mistakes: [], heard: '', lastHeardAt: 0, turns: [], error: '', watcher: null, typed: '' })

  const startTalk = async (topicOrLabel) => {
    const G = g.current
    const topic = TOPICS.find(t => t.label === topicOrLabel)?.key || topicOrLabel
    if (!topic) return
    stopAll()
    const T = newTalk()
    G.talk = T
    T.topic = topic
    const level = (learningRef.current || []).find(l => l.code === G.lang)?.level || 'A2'
    T.turns = [{ role: 'system', content: partnerPrompt({ target: languages[G.lang] || G.lang, own: languages[nativeRef.current] || 'English', level, topic }) }]
    await partnerTurn(T, true)
  }

  const partnerTurn = async (T, opening) => {
    const G = g.current
    T.state = 'thinking'
    update()
    try {
      const request = [...T.turns]
      if (opening) request.push({ role: 'user', content: '(Start the conversation: greet me and ask me one easy question about the topic. No correction yet.)' })
      // Only the system prompt and the last turns: long talks stay quick.
      const messages = request.length > 16 ? [request[0], ...request.slice(-15)] : request
      const { data } = await client.mutate({ mutation: CHAT_MUTATION, variables: { messages, model: CHAT_MODEL, json: true, maxTokens: 350, temperature: 0.7 } })
      if (G.talk !== T) return
      const turn = parseTurn(data?.chat?.text)
      if (!turn.reply) throw new Error('The model returned nothing usable — try again.')
      T.turns.push({ role: 'assistant', content: turn.reply })
      T.mistakes.push(...turn.mistakes)
      const line = { mine: false, text: turn.reply, fix: turn.fix, better: turn.better }
      T.lines.push(line)
      speakTalkLine(T, line)
    } catch (e) {
      if (G.talk !== T) return
      T.state = 'failed'
      T.error = /530|502|503|fetch/i.test(e.message) ? 'The Mac Studio isn’t answering — it may be asleep. Try again in a moment.' : e.message
      update()
    }
  }

  // The correction (in your language), the right way to say it, then the reply — then your turn.
  const speakTalkLine = async (T, line) => {
    const G = g.current
    const gen = ++G.gen
    clearInterval(T.watcher)
    T.state = 'speaking'
    update()
    if (line.fix) await say(line.fix, nativeRef.current)
    if (G.gen !== gen || G.talk !== T) return
    if (line.better) await say(line.better, G.lang)
    if (G.gen !== gen || G.talk !== T) return
    await say(line.text, G.lang)
    if (G.gen !== gen || G.talk !== T || T.state !== 'speaking') return
    listenTalk(T)
  }

  /** Your turn: the phone listens; a pause ends it. */
  const listenTalk = (T) => {
    T.heard = ''
    T.state = 'listening'
    clearInterval(T.watcher)
    T.watcher = setInterval(() => {
      if (g.current.talk !== T || T.state !== 'listening') { clearInterval(T.watcher); return }
      if (T.heard && Date.now() - T.lastHeardAt > PAUSE_TO_ANSWER_MS) finishTalkTurn(T)
    }, 200)
    update()
  }

  const finishTalkTurn = async (T) => {
    const said = T.heard.trim()
    if (T.state !== 'listening' || !said) return
    clearInterval(T.watcher)
    T.lines.push({ mine: true, text: said, fix: '', better: '' })
    T.turns.push({ role: 'user', content: said })
    T.heard = ''
    await partnerTurn(T, false)
  }

  const talkHeard = (text) => {
    const T = g.current.talk
    if (!T || T.state !== 'listening') return
    const t = String(text || '').trim()
    if (!t || t === T.heard) return
    T.heard = t
    T.lastHeardAt = Date.now()
    update()
  }

  /** Typed (here or on the phone): that's the whole turn. */
  const talkTyped = (text) => {
    const T = g.current.talk
    const t = String(text || '').trim()
    if (!T || !t || !['listening', 'paused', 'speaking'].includes(T.state)) return
    stopAll()
    T.heard = t
    T.state = 'listening'
    finishTalkTurn(T)
  }

  const talkPause = () => {
    const T = g.current.talk
    if (!T) return
    if (T.state === 'paused' || T.state === 'failed') {
      if (T.state === 'failed' && T.lines.length === 0) { startTalk(T.topic); return }
      if (T.state === 'failed' && T.lines[T.lines.length - 1]?.mine) { T.heard = T.lines.pop().text; T.turns.pop(); T.state = 'listening'; finishTalkTurn(T); return }
      listenTalk(T)
    } else if (T.state === 'listening' || T.state === 'speaking') {
      stopAll()
      T.state = 'paused'
      update()
    }
  }

  const talkAgain = () => {
    const T = g.current.talk
    const last = T && [...T.lines].reverse().find(l => !l.mine)
    if (last) speakTalkLine(T, last)
  }

  const talkCommand = (action, value) => {
    const T = g.current.talk
    switch (action) {
      case 'topic': case 'pick': if (value) startTalk(value); break
      case 'send': if (T && T.state === 'listening') finishTalkTurn(T); break
      case 'say': talkTyped(value); break
      case 'pause': case 'playPause': talkPause(); break
      case 'again': case 'hear': talkAgain(); break
      default: break
    }
  }

  // MARK: build a sentence

  const newBuild = () => ({
    phase: 'ask', text: '', build: null, error: '', recent: [], map: ['who', 'when', 'where', 'what', 'verb'],
    step: 0, options: [], wrong: new Set(), justBuilt: false, mistakes: 0, saved: false, round: 0,
    units: [], tokens: [], states: [], advance: null,
  })

  const currentBlock = () => {
    const B = g.current.build
    const blocks = B?.build?.blocks || []
    return B && B.phase === 'build' && B.step < blocks.length ? blocks[B.step] : null
  }
  const isBuilt = (i) => {
    const B = g.current.build
    return B.phase === 'done' || i < B.step || (i === B.step && B.justBuilt)
  }
  const builtWordHeard = (i) => {
    const B = g.current.build
    const u = B && B.units[i]
    if (!u || u.to <= u.from) return false
    for (let k = u.from; k < u.to; k++) if (B.states[k] !== 'hit') return false
    return true
  }
  const buildAllSaid = () => {
    const B = g.current.build
    return !!B && B.states.length > 0 && B.states.every(x => x === 'hit')
  }

  const loadBuilds = async () => {
    const G = g.current
    const B = G.build
    const lang = G.lang
    try {
      const [r, m] = await Promise.all([
        client.query({ query: MY_SENTENCE_BUILDS_QUERY, variables: { lang, limit: 8 }, fetchPolicy: 'network-only' }).catch(() => null),
        client.query({ query: SENTENCE_MAP_QUERY, variables: { lang } }).catch(() => null),
      ])
      if (G.build !== B) return
      B.recent = (r?.data?.mySentenceBuilds || []).filter(b => b.status === 'ready' && b.blocks?.length)
      if (m?.data?.sentenceMap?.length) B.map = m.data.sentenceMap
      update()
    } catch { /* the list is a nicety */ }
  }

  /** Asks the Mac Studio, and waits for it (it can take a little while). */
  const startBuild = async (text) => {
    const G = g.current
    const B = G.build
    const said = String(text ?? B.text).trim()
    if (!said || B.phase === 'making') return
    B.text = said
    B.error = ''
    B.phase = 'making'
    update()
    try {
      let b = (await client.mutate({ mutation: START_SENTENCE_BUILD_MUTATION, variables: { lang: G.lang, text: said } })).data.startSentenceBuild
      const t0 = Date.now()
      while (G.build === B && b.status === 'making' && Date.now() - t0 < 240000) {
        await sleep(1500)
        b = (await client.query({ query: SENTENCE_BUILD_QUERY, variables: { id: b.id }, fetchPolicy: 'network-only' })).data.sentenceBuild
        if (!b) throw new Error('That sentence went missing — try again.')
      }
      if (G.build !== B) return
      if (b.status === 'ready' && b.blocks?.length) openBuild(b)
      else { B.error = b.error || 'That took too long — try again in a moment.'; B.phase = 'ask'; update() }
    } catch (e) {
      if (G.build !== B) return
      B.error = e.message
      B.phase = 'ask'
      update()
    }
  }

  const openBuild = (b) => {
    const B = g.current.build
    B.build = b
    B.saved = !!b.saved
    B.text = b.text
    restartBuild()
  }

  /** The same sentence again, the options in a new order. */
  const restartBuild = () => {
    const B = g.current.build
    clearTimeout(B.advance)
    B.round += 1
    B.step = 0
    B.mistakes = 0
    B.error = ''
    B.phase = 'build'
    prepareBlock()
  }

  const newSentence = () => {
    stopAll()
    const old = g.current.build
    g.current.build = { ...newBuild(), recent: old?.recent || [], map: old?.map || newBuild().map }
    update()
    loadBuilds()
  }

  const prepareBlock = () => {
    const B = g.current.build
    B.wrong = new Set()
    B.justBuilt = false
    const cur = currentBlock()
    if (cur) {
      const o = cur.options?.length ? [...cur.options] : [cur.said]
      // Shuffled anew each time round: it's the form you choose, not the place.
      B.options = B.round > 1 ? shuffle(o) : o
    }
    update()
  }

  /** A choice: the right form builds the block; a wrong one is marked. */
  const pickBlock = (option) => {
    const B = g.current.build
    const cur = currentBlock()
    if (!cur || B.justBuilt || option == null) return
    if (String(option).trim().toLowerCase() === cur.said.trim().toLowerCase()) {
      B.justBuilt = true
      say(cur.said, g.current.lang)
      // Time to read why it's this form; straight on when there's nothing to
      // read. Up to A2 the note stays until Next; from B1 it's 2 seconds.
      const at = B.step
      const hasNote = !!(cur.note || cur.base)
      clearTimeout(B.advance)
      if (!(hasNote && beginnerIn(g.current.lang))) {
        B.advance = setTimeout(() => { if (g.current.build === B && B.step === at && B.justBuilt) nextBlock() }, hasNote ? 2000 : 1200)
      }
    } else if (!B.wrong.has(option)) {
      B.wrong.add(option)
      B.mistakes += 1
    }
    update()
  }

  const nextBlock = () => {
    const B = g.current.build
    clearTimeout(B.advance)
    if (B.phase !== 'build') return
    B.step += 1
    if (B.step >= (B.build?.blocks || []).length) finishBuild(); else prepareBlock()
  }

  const finishBuild = () => {
    const G = g.current
    const B = G.build
    B.phase = 'done'
    B.justBuilt = false
    const { units, tokens, spaced } = wordUnits(B.build?.sentence || '', G.lang)
    B.units = units
    B.tokens = tokens
    B.spaced = spaced
    B.states = tokens.map(() => 'pending')
    update()
    hearBuild()
  }

  const hearBuild = () => {
    const B = g.current.build
    if (B?.build?.sentence) say(B.build.sentence, g.current.lang)
  }

  const saveBuild = async () => {
    const B = g.current.build
    if (!B?.build?.id || B.saved) return
    try {
      await client.mutate({ mutation: SAVE_SENTENCE_BUILD_MUTATION, variables: { id: B.build.id } })
      B.saved = true
    } catch (e) { B.error = e.message }
    update()
  }

  /** While building, the block said builds it; at the end, the words light up. */
  const buildHeard = (transcript) => {
    const G = g.current
    const B = G.build
    if (!B) return
    if (B.phase === 'build') {
      const cur = currentBlock()
      if (!cur || B.justBuilt) return
      const want = tokenizeLine(cur.said, G.lang)
      const got = tokenizeTranscript(transcript, G.lang)
      if (want.length && want.every(w => got.some(t => tokenMatches(t, w.norm, G.lang)))) pickBlock(cur.said)
    } else if (B.phase === 'done' && B.tokens.length) {
      B.states = mergeTranscript(B.states, transcript, B.tokens, G.lang, false)
      update()
    }
  }

  const buildCommand = (action, value) => {
    const B = g.current.build
    switch (action) {
      case 'build':
        if (value && value.trim()) {
          if (B.phase !== 'ask') newSentence()
          startBuild(value)
        }
        break
      case 'recent': { const b = B.recent.find(r => r.id === value); if (b) openBuild(b); break }
      case 'pick': pickBlock(value); break
      case 'next': nextBlock(); break
      case 'again': if (B.phase === 'build' || B.phase === 'done') restartBuild(); break
      case 'new': newSentence(); break
      case 'save': saveBuild(); break
      case 'hear': if (B.phase === 'done') hearBuild(); else { const cur = currentBlock(); if (cur) say(cur.said, g.current.lang) } break
      default: break
    }
  }

  // MARK: moving between screens

  const open = (screen, first) => {
    const G = g.current
    if (!MODES.some(m => m.screen === screen)) { publish(true); return }
    stopAll()
    G.screen = screen
    if (screen === 'listen') {
      G.listen = { order: ordered(G.deck, first), index: 0, paused: false, meaningShown: false }
      startListen()
    } else if (screen === 'gaps' || screen === 'memory') {
      beginSay(screen === 'memory', null, first)
    } else if (screen === 'quiz') {
      beginQuiz(first)
    } else if (screen === 'talk') {
      G.talk = newTalk()
    } else if (screen === 'build') {
      G.build = newBuild()
      loadBuilds()
    }
    update()
  }

  const goHome = () => {
    stopAll()
    leaveHomework()
    g.current.screen = 'home'
    publish(true)
    render()
  }

  const command = (c) => {
    const G = g.current
    if (!c || !c.action) return
    const { action, value } = c
    if (action === 'audio') { G.audioOnPhone = value === 'phone'; if (!G.audioOnPhone) finishAllSpeech(); return }
    if (action === 'speed') { const v = parseFloat(value); if (v > 0) G.speed = v; return }
    if (action === 'open') return open(value)
    if (action === 'home') return goHome()
    if (action === 'lang') return setLang(value)
    if (action === 'homework') { openHomework(value); return }
    if (action === 'signOut') return
    switch (G.screen) {
      case 'home':
        // Picked on the phone with nothing open: practise it, filling the gaps.
        if (action === 'phrase' && value) open('gaps', value)
        else publish(true)
        break
      case 'listen':
        if (action === 'back') listenGo(-1)
        else if (action === 'next') listenGo(1)
        else if (action === 'playPause') listenPause()
        else if (action === 'phrase') {
          const L = G.listen
          const i = L.order.findIndex(p => p.id === value)
          if (i >= 0) listenGo(i - L.index)
        }
        break
      case 'gaps': case 'memory': sayCommand(action, value); break
      case 'quiz': quizCommand(action, value); break
      case 'talk': talkCommand(action, value); break
      case 'build': buildCommand(action, value); break
      default: publish(true)
    }
  }

  // MARK: the connection

  useEffect(() => {
    const G = g.current
    G.closing = false
    let retry = null

    const connect = () => {
      let token = ''
      try { token = localStorage.getItem('token') || '' } catch { /* private window */ }
      if (!token) { G.status = 'error'; G.error = 'Sign in first.'; render(); return }
      G.status = 'connecting'
      G.paired = false
      render()
      let ws
      try { ws = new WebSocket(relayUrl()) } catch (e) { G.status = 'error'; G.error = e.message; render(); return }
      G.ws = ws
      ws.onopen = () => ws.send(JSON.stringify({ relay: 'open', token }))
      ws.onmessage = (ev) => {
        let m
        try { m = JSON.parse(ev.data) } catch { return }
        if (m.relay) {
          if (m.relay === 'opened') {
            G.session = m.session
            G.code = m.code
            G.status = 'open'
            G.error = ''
            QRCode.toDataURL(`communicate://web-remote?session=${encodeURIComponent(m.session)}&code=${m.code}`,
              { margin: 1, width: 560, color: { dark: '#0a0b12', light: '#ffffff' } })
              .then(setQr).catch(() => setQr(''))
          } else if (m.relay === 'gone') {
            // The phone went: carry on here, and show the code again.
            G.paired = false
            G.phoneName = null
            G.audioOnPhone = false
            finishAllSpeech()
          } else if (m.relay === 'error') {
            G.status = 'error'
            G.error = m.error || 'The server said no.'
          }
          render()
          return
        }
        switch (m.kind) {
          case 'hello':
            if (m.code === G.code) {
              G.paired = true
              G.phoneName = m.name || 'iPhone'
              G.lastSent = ''
              send({ kind: 'welcome', name: 'Big screen' })
              publish(true)
            } else {
              send({ kind: 'refused' })
            }
            break
          case 'command': command(m.command); break
          case 'heard': heard(m.heard || ''); break
          case 'spoke':
            finishSpeech(m.speechId)
            if (G.saying != null) { G.saying = null; publish() }
            break
          case 'saying':
            G.saying = m.wordIndex == null ? null : m.wordIndex
            publish()
            break
          default: break
        }
        render()
      }
      ws.onclose = () => {
        if (G.ws !== ws) return
        G.ws = null
        G.paired = false
        G.phoneName = null
        G.audioOnPhone = false
        finishAllSpeech()
        setQr('')
        if (G.closing) return
        if (G.status !== 'error') G.status = 'connecting'
        render()
        // The server restarting, the Wi-Fi blinking: a new session, a new code.
        retry = setTimeout(connect, G.status === 'error' ? 8000 : 2500)
      }
    }
    connect()

    // Keep the screen awake while it's in use, where the browser allows.
    let lock = null
    navigator.wakeLock?.request?.('screen').then(l => { lock = l }).catch(() => { /* not allowed */ })

    return () => {
      G.closing = true
      clearTimeout(retry)
      G.gen++
      stopSpeaking()
      finishAllSpeech()
      if (G.ws) { try { G.ws.close() } catch { /* already closed */ } }
      G.ws = null
      lock?.release?.().catch(() => { /* already released */ })
    }
  }, [])

  // The deck, once the learner's languages are known.
  const firstLang = myLangs[0] || ''
  useEffect(() => {
    const G = g.current
    if (!G.lang && firstLang) G.lang = firstLang
    if (G.lang && !G.deck.length && !G.loading) loadDeck(G.lang)
  }, [firstLang])
  useEffect(() => { loadHomework() }, []) // eslint-disable-line

  // The keyboard, for using it without a phone.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /input|textarea/i.test(e.target.tagName)) return
      const G = g.current
      if (e.key === 'Escape') { if (G.screen === 'home') onClose(); else goHome(); return }
      if (G.screen === 'listen') {
        if (e.key === ' ') { e.preventDefault(); listenPause() }
        if (e.key === 'ArrowRight') listenGo(1)
        if (e.key === 'ArrowLeft') listenGo(-1)
      } else if (G.screen === 'gaps' || G.screen === 'memory') {
        const S = G.say
        if (e.key === ' ') { e.preventDefault(); if (!S.revealed) revealSay(); else answerSay(true) }
        if (e.key.toLowerCase() === 'h') sayCommand('hint')
        if (e.key === 'ArrowRight') sayCommand('next')
      } else if (G.screen === 'quiz') {
        const n = parseInt(e.key, 10)
        if (n >= 1 && n <= 4 && G.quiz.options[n - 1]) chooseQuiz(G.quiz.options[n - 1])
        if (e.key === 'ArrowRight' || e.key === 'Enter') quizCommand('next')
      } else if (G.screen === 'talk') {
        if (e.key === ' ') { e.preventDefault(); talkPause() }
      } else if (G.screen === 'build' && G.build) {
        const n = parseInt(e.key, 10)
        if (n >= 1 && n <= G.build.options.length && currentBlock()) pickBlock(G.build.options[n - 1])
        if ((e.key === 'Enter' || e.key === 'ArrowRight') && G.build.justBuilt) nextBlock()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // MARK: drawing it

  const G = g.current
  const langName = languages[lang] || lang
  const fullScreen = () => {
    const el = document.documentElement
    if (document.fullscreenElement) document.exitFullscreen?.()
    else el.requestFullscreen?.().catch(() => { /* not allowed here */ })
  }

  const qrCard = (big) => (
    <div className={`${s.qrCard} ${big ? s.qrBig : s.qrSmall}`}>
      {G.status === 'open' && qr ? <img src={qr} alt="QR code to pair your iPhone" className={s.qr} /> : <div className={s.qrWait}>{G.status === 'error' ? '!' : '…'}</div>}
      <div className={s.qrText}>
        {G.status === 'error'
          ? <><b>Can’t open the big screen</b><span>{G.error}</span></>
          : <><b>Use your iPhone as the remote</b>
            <span>Scan this with the iPhone’s camera — Communicate opens as the remote, with the microphone.</span>
            {G.code && <span className={s.code}>Code {G.code}</span>}</>}
      </div>
    </div>
  )

  // A phrase, word by word: gaps, heard words green, the word being said lit.
  const phraseWords = (S, p) => {
    const roman = !['cmn', 'jpn'].includes(G.lang)
    return (
      <div className={`${s.words} ${S.spaced ? '' : s.unspaced}`}>
        {S.units.map((u, i) => {
          const real = u.to > u.from
          const heardIt = wordHeard(i)
          const gap = S.hidden.has(i) && !S.revealed && !heardIt
          const missed = S.revealed && real && S.states.some(x => x === 'hit') && !heardIt
          const now = G.saying === i && real && !gap
          const r = roman && !gap && real ? romanizationOf(u.raw, G.lang) : null
          if (gap) {
            const shown = hintPrefix(u.raw, S.hint)
            const rest = Math.min(Math.max(Array.from(u.raw).length - shown, 1), 7)
            return (
              <span key={i} className={s.wordBox}>
                <span className={s.gap}>
                  {shown > 0 && <span className={s.hint}>{Array.from(u.raw).slice(0, shown).join('')}</span>}
                  <span style={{ display: 'inline-block', width: `${0.4 * rest}em` }} />
                </span>
                <span className={s.roman}>&nbsp;</span>
              </span>
            )
          }
          const cls = [s.word, !real && s.punct, heardIt && s.heard, missed && s.missed, now && s.now, S.hidden.has(i) && !heardIt && !missed && s.filled].filter(Boolean).join(' ')
          return (
            <span key={i} className={s.wordBox}>
              <span className={cls}>{u.raw}</span>
              <span className={s.roman}>{r || ' '}</span>
            </span>
          )
        })}
      </div>
    )
  }

  const progress = (index, total) => (
    <div className={s.progress}>
      <div className={s.bar}><div style={{ width: `${total ? Math.min(100, ((index + 1) / total) * 100) : 0}%` }} /></div>
      <span>{Math.min(index + 1, total)} / {total}</span>
    </div>
  )

  let body = null
  if (G.screen === 'home') {
    body = (
      <div className={s.home}>
        <div className={s.homeMain}>
          <h1 className={s.hello}>Practise on the big screen</h1>
          <p className={s.sub}>{G.paired
            ? `${G.phoneName} is the remote — choose on the phone, and say things out loud as you go.`
            : 'Your phrases, big — with your iPhone as the remote and the microphone. Or use the mouse and keyboard.'}</p>
          {myLangs.length > 1 && (
            <div className={s.chips}>
              {myLangs.map(c => (
                <button key={c} className={`${s.chip} ${c === lang ? s.chipOn : ''}`} onClick={() => setLang(c)}>{languages[c] || c}</button>
              ))}
            </div>
          )}
          <div className={s.modes}>
            {MODES.map(m => (
              <button key={m.screen} className={s.mode} disabled={!G.deck.length && !['talk', 'build'].includes(m.screen)} onClick={() => open(m.screen)}>
                <b>{m.title}</b><span>{m.blurb}</span>
              </button>
            ))}
          </div>
          <p className={s.note}>{G.loading ? `Loading your ${langName} phrases…`
            : G.deck.length ? `${G.deck.length} phrases in ${langName}`
              : lang ? `No phrases in ${langName} yet — add some, then come back.` : ''}</p>
        </div>
        {!G.paired && qrCard(true)}
      </div>
    )
  } else if (G.screen === 'listen') {
    const L = G.listen
    const p = L.order[L.index]
    const r = p && romanizationOf(p.text, G.lang)
    body = p && (
      <div className={s.stage}>
        {progress(L.index, L.order.length)}
        <div className={s.centre}>
          <div className={s.big}>{p.text}</div>
          {r && <div className={s.romanLine}>{r}</div>}
          <div className={`${s.meaning} ${L.meaningShown ? s.shown : s.hiddenMeaning}`}>{p.meaning || ' '}</div>
        </div>
        <div className={s.controls}>
          <button className={s.round} onClick={() => listenGo(-1)} aria-label="Back">◀◀</button>
          <button className={`${s.round} ${s.roundMain}`} onClick={listenPause} aria-label={L.paused ? 'Play' : 'Pause'}>{L.paused ? '▶' : '❚❚'}</button>
          <button className={s.round} onClick={() => listenGo(1)} aria-label="Next">▶▶</button>
        </div>
      </div>
    )
  } else if (G.screen === 'gaps' || G.screen === 'memory') {
    const S = G.say
    const p = S && S.round[S.index]
    if (p) {
      body = (
        <div className={s.stage}>
          {progress(S.index, S.round.length)}
          <div className={s.centre} key={S.index}>
            {p.meaning && (G.screen === 'memory' || !S.revealed) && <div className={s.prompt}>{p.meaning}</div>}
            {phraseWords(S, p)}
            {S.revealed && p.meaning && G.screen !== 'memory' && <div className={`${s.meaning} ${s.shown}`}>{p.meaning}</div>}
          </div>
          <div className={s.status}>
            {S.saidRight ? <span className={S.hint ? s.yellow : s.green}>{S.hint ? 'All of it — with a hint.' : 'All of it!'}</span>
              : S.practisedAll ? <span className={s.green}>All green — well said.</span>
                : practising() ? <span>{G.paired ? 'Now read it out — get every word green.' : 'Now read it out loud.'}</span>
                  : S.nearly && !S.revealed ? <span className={s.green}>Nearly all of it — say the rest, or move on.</span>
                    : <span>{G.paired ? `Say it into ${G.phoneName} — the words light up as you’re heard.` : 'Say the whole phrase out loud, then check.'}</span>}
          </div>
          <div className={s.buttons}>
            {S.revealed ? (
              <>
                <button className={s.btn} onClick={() => say(p.text, G.lang)}>Hear it again</button>
                <button className={s.btn} onClick={() => answerSay(false)}>Not yet</button>
                <button className={`${s.btn} ${s.primary}`} onClick={() => answerSay(true)}>{S.saidRight || S.practisedAll ? 'Next' : 'Got it ✓'}</button>
              </>
            ) : (
              <>
                {S.hint < 2 && S.hidden.size > 0 && <button className={s.btn} onClick={() => sayCommand('hint')}>{S.hint ? 'Another hint' : 'Hint'}</button>}
                <button className={`${s.btn} ${s.primary}`} onClick={revealSay}>Show the answer</button>
                {S.nearly && <button className={s.btn} onClick={() => answerSay(true)}>Got it ✓</button>}
              </>
            )}
          </div>
        </div>
      )
    } else if (S) {
      const left = toPractise()
      body = (
        <div className={s.done}>
          <div className={s.doneCount}>{saidCount()} of {thisRound().length} said in full</div>
          {helpedCount() > 0 && <div className={s.yellow}>{helpedCount()} more with help — hints, or after the answer</div>}
          {left.length > 0 && (
            <div className={s.practise}>
              <div className={s.label}>To practise</div>
              {left.slice(0, 6).map(x => <div key={x.id} className={s.practiseRow}>{x.text}</div>)}
              {left.length > 6 && <div className={s.note}>and {left.length - 6} more</div>}
            </div>
          )}
          <div className={s.buttons}>
            {left.length > 0 && <button className={`${s.btn} ${s.primary}`} onClick={() => sayCommand('againMissed')}>Practise these again</button>}
            <button className={s.btn} onClick={() => sayCommand('again')}>Go again</button>
            <button className={s.btn} onClick={goHome}>Home</button>
          </div>
        </div>
      )
    }
  } else if (G.screen === 'quiz') {
    const Q = G.quiz
    const p = Q && Q.round[Q.index]
    if (Q && quizPool().length < 4) {
      body = <div className={s.done}><div className={s.sub}>A quiz needs at least four phrases with meanings.</div><button className={s.btn} onClick={goHome}>Home</button></div>
    } else if (p) {
      const r = romanizationOf(p.text, G.lang)
      body = (
        <div className={s.stage}>
          {progress(Q.index, Q.round.length)}
          <div className={s.centre}>
            <div className={s.big}>{p.text}</div>
            {r && <div className={s.romanLine}>{r}</div>}
            <button className={s.btn} onClick={() => say(p.text, G.lang)}>Hear it</button>
          </div>
          <div className={s.options}>
            {Q.options.map((o, i) => {
              const isAnswer = Q.picked != null && o === p.meaning
              const isPick = Q.picked === o && !isAnswer
              return (
                <button key={o} className={`${s.option} ${isAnswer ? s.optionRight : ''} ${isPick ? s.optionWrong : ''}`}
                  disabled={Q.picked != null} onClick={() => chooseQuiz(o)}>
                  <span className={s.key}>{i + 1}</span>{o}
                </button>
              )
            })}
          </div>
          {Q.picked != null && <div className={s.buttons}><button className={`${s.btn} ${s.primary}`} onClick={() => quizCommand('next')}>{Q.index + 1 < Q.round.length ? 'Next' : 'Finish'}</button></div>}
        </div>
      )
    } else if (Q) {
      body = (
        <div className={s.done}>
          <div className={s.doneCount}>{Q.right} of {Q.round.length} right</div>
          <div className={s.buttons}>
            <button className={`${s.btn} ${s.primary}`} onClick={() => beginQuiz()}>Go again</button>
            <button className={s.btn} onClick={goHome}>Home</button>
          </div>
        </div>
      )
    }
  }

  if (G.screen === 'talk' && G.talk) {
    const T = G.talk
    if (T.state === 'choosing') {
      body = (
        <div className={s.ask}>
          <h1 className={s.hello}>What shall we talk about?</h1>
          <p className={s.sub}>{G.paired
            ? `Answer by speaking into ${G.phoneName} — or type on it, or here.`
            : 'Type your answers here — or scan the code to answer out loud with your iPhone.'}</p>
          <div className={s.modes}>
            {TOPICS.map(t => <button key={t.key} className={s.mode} onClick={() => startTalk(t.key)}><b>{t.label}</b></button>)}
          </div>
        </div>
      )
    } else {
      const status = T.state === 'thinking' ? <span>Thinking…</span>
        : T.state === 'listening' ? <span>{G.paired ? `Your turn — speak into ${G.phoneName}, or type.` : 'Your turn — type your answer.'}</span>
          : T.state === 'paused' ? <span>Paused — carry on when you’re ready.</span>
            : T.state === 'failed' ? <span className={s.red}>{T.error}</span> : null
      body = (
        <div className={s.talk}>
          <div className={s.chat}>
            {T.lines.map((l, i) => (
              <div key={i} className={`${s.line} ${l.mine ? s.mine : ''}`}>
                {l.fix && <div className={s.fix}><span>{l.fix}</span><b>{l.better}</b></div>}
                <div className={s.bubble}>{l.text}</div>
              </div>
            ))}
            {T.state === 'listening' && T.heard && <div className={`${s.line} ${s.mine}`}><div className={`${s.bubble} ${s.heardBubble}`}>{T.heard}</div></div>}
            <div className={s.status}>{status}</div>
            <form className={s.typeRow} onSubmit={(e) => { e.preventDefault(); talkTyped(talkDraft); setTalkDraft('') }}>
              <input className={s.input} value={talkDraft} onChange={e => setTalkDraft(e.target.value)} placeholder="Or type your answer" />
              <button className={`${s.btn} ${s.primary}`} disabled={!talkDraft.trim()}>Send</button>
            </form>
            <div className={s.buttons}>
              <button className={s.btn} onClick={talkAgain}>Hear it again</button>
              <button className={s.btn} onClick={talkPause}>{T.state === 'paused' || T.state === 'failed' ? (T.state === 'failed' ? 'Try again' : 'Carry on') : 'Pause'}</button>
              {T.state === 'listening' && T.heard && <button className={s.btn} onClick={() => finishTalkTurn(T)}>Done — send</button>}
            </div>
          </div>
          {T.mistakes.length > 0 && (
            <div className={s.corrections}>
              <div className={s.label}>Corrections</div>
              {T.mistakes.slice(-6).map((m, i) => (
                <div key={i} className={s.correction}>
                  <del>{m.said}</del>
                  <b>{m.better}</b>
                  {m.why && <span>{m.why}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )
    }
  }

  if (G.screen === 'build' && G.build) {
    const B = G.build
    const blocks = B.build?.blocks || []
    const cur = currentBlock()
    const slots = (
      <div className={s.slots}>
        {(blocks.length ? blocks : B.map.map(role => ({ role }))).map((b, i) => {
          const built = blocks.length && isBuilt(i)
          const now = B.phase === 'build' && i === B.step && !B.justBuilt
          return (
            <div key={`${i}-${B.round}`} className={`${s.slot} ${now ? s.slotNow : ''} ${built ? s.slotBuilt : ''} ${B.phase === 'making' ? s.shimmer : ''} ${B.explain === i ? s.slotOpen : ''}`}
              style={{ '--role': roleColor(b.role), animationDelay: `${i * 0.15}s` }}
              // A built block: click it to see why it's this form.
              role={built ? 'button' : undefined} tabIndex={built ? 0 : undefined} title={built ? 'Why it’s this form' : undefined}
              onClick={built ? () => { B.explain = B.explain === i ? null : i; update() } : undefined}>
              <span className={s.slotRole}>{roleAsk(b.role)}</span>
              <span className={s.slotText}>{built ? b.said : B.phase === 'making' ? '…' : '\u00a0'}</span>
              <span className={s.slotMeaning}>{b.meaning || '\u00a0'}</span>
            </div>
          )
        })}
      </div>
    )
    // Why a built block is the form it is: its meaning, dictionary form and note.
    const open = blocks.length && B.explain != null && isBuilt(B.explain) ? blocks[B.explain] : null
    const explanation = open && (
      <div className={s.explain} style={{ '--role': roleColor(open.role) }}>
        <button className={s.explainClose} onClick={() => { B.explain = null; update() }} aria-label="Close">×</button>
        <div className={s.slotRole}>{roleAsk(open.role)}</div>
        <div className={s.explainSaid}>{open.said}{romanizationOf(open.said, G.lang) && <small>{romanizationOf(open.said, G.lang)}</small>}</div>
        {open.meaning && <div className={s.explainMeaning}>{open.meaning}</div>}
        {open.base && <div className={s.explainBase}>↩ from <b>{open.base}</b></div>}
        {open.note ? <div className={s.explainNote}>{open.note}</div> : !open.base && <div className={s.explainMeaning}>Nothing changes here — it’s said as it is.</div>}
      </div>
    )
    if (B.phase === 'ask') {
      body = (
        <div className={s.ask}>
          <h1 className={s.hello}>What do you want to say?</h1>
          <p className={s.sub}>{G.paired
            ? `Say it into ${G.phoneName} in your own language — or type it — and build it here in ${langName}, one block at a time.`
            : `Type it in ${languages[nativeCode] || 'your own language'}, and build it in ${langName} one block at a time. With your iPhone as the remote, you can just say it.`}</p>
          <form className={s.typeRow} onSubmit={(e) => { e.preventDefault(); if (buildDraft.trim()) { startBuild(buildDraft); setBuildDraft('') } }}>
            <input className={s.input} value={buildDraft} onChange={e => setBuildDraft(e.target.value)} placeholder="e.g. I’m drinking coffee at the café with my friend" />
            <button className={`${s.btn} ${s.primary}`} disabled={!buildDraft.trim()}>Build it →</button>
          </form>
          {B.error && <div className={s.red}>{B.error}</div>}
          <div className={s.platter}>
            <div className={s.label}>The safe beginner map in {langName}</div>
            <div className={s.map}>
              {B.map.map((r, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className={s.plus}>+</span>}
                  <span className={s.mapRole} style={{ '--role': roleColor(r) }}>{r === 'verb' ? 'VERB' : roleAsk(r).replace('?', '').toUpperCase()}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
          {B.recent.length > 0 && (
            <>
              <div className={s.label}>Your sentences</div>
              <div className={s.recent}>
                {B.recent.map(r => (
                  <button key={r.id} className={s.mode} onClick={() => openBuild(r)}>
                    <b>{r.sentence}</b><span>{r.text}{r.saved ? ' · ★' : ''}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )
    } else if (B.phase === 'making') {
      body = (
        <div className={s.done}>
          <div className={s.prompt}>{B.text}</div>
          {slots}
          <div className={s.sub}>Building it in {langName} on the Mac Studio…</div>
        </div>
      )
    } else if (B.phase === 'build' && cur) {
      body = (
        <div className={s.stage}>
          <div className={s.want}>{B.text}</div>
          {slots}
          {explanation}
          <div className={s.centre} key={`${B.step}-${B.round}`}>
            <div className={s.question}><span style={{ color: roleColor(cur.role) }}>{roleAsk(cur.role)}</span>{cur.meaning && <span className={s.questionMeaning}>“{cur.meaning}”</span>}</div>
            <div className={s.blockOptions}>
              {B.options.map((o, i) => {
                const isWrong = B.wrong.has(o)
                const isRight = B.justBuilt && o.toLowerCase() === cur.said.toLowerCase()
                const r = romanizationOf(o, G.lang)
                return (
                  <button key={`${o}-${i}`} disabled={(B.justBuilt && !isRight) || isWrong}
                    className={`${s.option} ${isWrong ? s.optionWrong : ''} ${isRight ? s.optionRight : ''}`} onClick={() => pickBlock(o)}>
                    <span className={s.key}>{i + 1}</span>
                    <span className={s.optionText}>{o}{r && <small>{r}</small>}</span>
                  </button>
                )
              })}
            </div>
            {B.justBuilt && (cur.note || cur.base) && (
              <div className={s.blockNote}><b style={{ color: roleColor(cur.role) }}>{cur.said}</b>{cur.base && <span> ← {cur.base}</span>}{cur.note && <> — {cur.note}</>}</div>
            )}
            {!B.justBuilt && B.wrong.size >= 2 && cur.note && <div className={`${s.blockNote} ${s.yellow}`}>💡 {cur.note}</div>}
            {!B.justBuilt && G.paired && <div className={s.note}>Choose — or say it into {G.phoneName}</div>}
          </div>
          {B.justBuilt && <div className={s.buttons}><button className={`${s.btn} ${s.primary}`} onClick={nextBlock}>Next</button></div>}
        </div>
      )
    } else if (B.phase === 'done') {
      const allSaid = buildAllSaid()
      body = (
        <div className={s.stage}>
          {slots}
          {explanation}
          <div className={s.centre}>
            <div className={s.green}>{B.mistakes === 0 ? '✓ Built it first time!' : '✓ Built it!'}</div>
            <div className={`${s.words} ${B.spaced === false ? s.unspaced : ''}`}>
              {B.units.map((u, i) => {
                const heardIt = builtWordHeard(i)
                const r = u.to > u.from ? romanizationOf(u.raw, G.lang) : null
                return (
                  <span key={i} className={s.wordBox}>
                    <span className={[s.word, u.to <= u.from && s.punct, heardIt && s.heard, G.saying === i && s.now].filter(Boolean).join(' ')}>{u.raw}</span>
                    <span className={s.roman}>{r || '\u00a0'}</span>
                  </span>
                )
              })}
            </div>
            {B.build?.meaning && <div className={`${s.meaning} ${s.shown}`}>{B.build.meaning}</div>}
            <div className={s.status}>{allSaid ? <span className={s.green}>All of it — well said!</span> : G.paired ? <span>Now say it all into {G.phoneName}</span> : null}</div>
          </div>
          <div className={s.buttons}>
            <button className={s.btn} onClick={hearBuild}>Hear it</button>
            <button className={s.btn} disabled={B.saved} onClick={saveBuild}>{B.saved ? '★ In your phrases' : '☆ Keep it'}</button>
            <button className={s.btn} onClick={restartBuild}>Build it again</button>
            <button className={`${s.btn} ${s.primary}`} onClick={newSentence}>Something else</button>
          </div>
          {B.error && <div className={`${s.red} ${s.status}`}>{B.error}</div>}
        </div>
      )
    }
  }

  const modeTitle = MODES.find(m => m.screen === G.screen)?.title
  return (
    <div className={s.view}>
      <div className={s.inner}>
        <div className={s.top}>
          <button className={`${s.btn} ${s.small}`} onClick={() => (G.screen === 'home' ? onClose() : goHome())}>
            {G.screen === 'home' ? '← Close' : '← Home'}
          </button>
          <div className={s.title}>{modeTitle ? `${modeTitle} · ` : 'Big screen · '}{langName}</div>
          <div className={s.topRight}>
            <span className={`${s.pill} ${G.paired ? s.pillOn : ''}`}>{G.paired ? `📱 ${G.phoneName}` : G.status === 'open' ? 'No remote yet' : G.status === 'error' ? 'Offline' : 'Connecting…'}</span>
            <button className={`${s.btn} ${s.small}`} onClick={fullScreen}>Full screen</button>
          </div>
        </div>
        {body}
        {G.screen !== 'home' && !G.paired && qrCard(false)}
      </div>
    </div>
  )
}
